import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const EXIT_MS = 160;

const Modal = ({ open, onClose, title, description, children, footer, size = "md" }) => {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [open, mounted]);

  // Callers pass a new onClose each render; reading it through a ref keeps
  // focus from jumping back to the dialog while someone is typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    if (!dialogRef.current?.contains(document.activeElement)) dialogRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onCloseRef.current?.();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <div className={`cc-modal-root cc-bb${closing ? " is-closing" : ""}`}>
      <div className="cc-scrim" onClick={onClose} />
      <div
        ref={dialogRef}
        className={`cc-modal cc-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className={`cc-modal__header${children ? "" : " cc-modal__header--solo"}`}>
          <div>
            <h2 className="cc-modal__title">{title}</h2>
            {description && <p className="cc-modal__description">{description}</p>}
          </div>
          <button type="button" className="cc-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        {children && <div className="cc-modal__body">{children}</div>}
        {footer && <div className="cc-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
